import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { SERIF, UI, MONO } from '../design/theme';
import PetAvatar from '../design/components/PetAvatar';
import PillButton from '../design/components/PillButton';
import SunsetGradient from '../design/components/SunsetGradient';
import PawIcon from '../design/components/PawIcon';
import CommunityActivityBoard from './components/CommunityActivityBoard';
import CommunityChallengeCard from './components/CommunityChallengeCard';
import { MOCK_TRIVIA } from './mockData';
import { useMeState, useMyPet } from './hooks';
import { getStoredPet } from './storage';
import { API, V2_API, apiGet, apiPost } from './api';
import { getStoredPersona, DEFAULT_PERSONA } from './persona';
import { resolvePhotoSrc } from './photoUrl';
import { shareApp, sharePet } from './share';
import { getChallengeGuestId } from './challengeUtils';
import { toast } from '../design/components/Toast';

const TYPE_META = {
  help_request: { emoji: '🤝', tone: 'amber', label: 'ช่วยกัน', action: 'ขอความช่วยเหลือในชุมชน', cta: 'เข้าไปช่วย' },
  location_update: { emoji: '📍', tone: 'blue', label: 'ตำแหน่ง', action: 'อัปเดตตำแหน่งน้อง', cta: 'เปิดดู' },
  regular_photo: { emoji: '📸', tone: 'pink', label: 'รูปใหม่', action: 'แชร์ภาพใหม่ในชุมชน', cta: 'ดูโมเมนต์' },
  regular: { emoji: '🐾', tone: 'green', label: 'อัปเดต', action: 'มีอัปเดตใหม่ในชุมชน', cta: 'ตามต่อ' },
};
const DEFAULT_ADMIN_EMAILS = new Set(['intaka@hotmail.com', 'intaka@gmail.com']);

// Random friendly mood emoji used as a fallback only when we have a real photo to show
const MOOD_POOL = ['🥰', '😺', '🦴', '💤', '😊', '🐾', '🌸', '✨', '🌳'];
const pickMood = (seed = '') => MOOD_POOL[(seed.charCodeAt(0) || 0) % MOOD_POOL.length];

const photoSrcOf = (p) => resolvePhotoSrc(p, 'medium');

function orderByPinned(items, pinnedIds = [], key = 'id') {
  const pins = (pinnedIds || []).map(String);
  return [...items].sort((a, b) => {
    const ai = pins.indexOf(String(a?.[key]));
    const bi = pins.indexOf(String(b?.[key]));
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return 0;
  });
}

function timeAgo(iso) {
  try {
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'เมื่อสักครู่';
    if (diff < 3600) return `${Math.floor(diff / 60)} นาที`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ชม.`;
    return `${Math.floor(diff / 86400)} วัน`;
  } catch { return 'ไม่นาน'; }
}

function activityFromPost(post, index) {
  const type = post.post_type || 'regular';
  const key = type === 'regular' && post.photos?.length ? 'regular_photo' : type;
  const meta = TYPE_META[key] || TYPE_META.regular;
  const photo = post.photos?.length ? resolvePhotoSrc(post.photos[0], 'medium') : null;
  const score = communityScore(post);
  const popularityLabel = score >= 20 ? 'แมวซุปตา' : score >= 12 ? 'กำลังฮิต' : (post.helpful_by?.length || 0) >= 2 ? 'ชุมชนยืนยัน' : meta.label;
  return {
    id: post.id || `post-${index}`,
    emoji: meta.emoji,
    name: post.created_by_name || 'เพื่อนในชุมชน',
    action: meta.action,
    when: timeAgo(post.created_at),
    tone: meta.tone,
    label: popularityLabel,
    cta: score >= 12 ? 'ดูว่าทำไมฮิต' : meta.cta,
    score,
    photo,
    route: post.animal_id ? `/v2/animal/${post.animal_id}` : '/v2/community',
  };
}

function communityScore(post) {
  const ageHours = Math.max(0, (Date.now() - new Date(post.created_at || Date.now()).getTime()) / 36e5);
  const recency = Math.max(0, 10 - Math.min(10, ageHours / 6));
  const likes = (post.likes || []).length * 3;
  const comments = (post.comments || []).filter(c => c.status !== 'deleted').length * 4;
  const photos = (post.photos || []).length ? 5 : 0;
  const helpful = (post.helpful_by || []).length * 5;
  const inaccurate = (post.inaccurate_by || []).length * -4;
  const reports = (post.reports || []).length * -8;
  return Math.round(recency + likes + comments + photos + helpful + inaccurate + reports);
}

function isMarketingSafePost(post) {
  const text = `${post.content || ''} ${post.created_by_name || ''}`.trim();
  if (!text) return true;
  return !/(^|\W)test(\W|$)|test[_\s-]|regression|iteration[_\s-]?\d+|debug[_\s-]?test|debug animal/i.test(text);
}

function isMarketingSafeAnimalName(name = '') {
  return !/test|debug|regression|iteration/i.test(String(name));
}

export default function HomeFeed({ theme }) {
  const navigate = useNavigate();
  // Show debug button only to admin users
  let isAdmin = false;
  try {
    const u = JSON.parse(localStorage.getItem('taillink_user') || 'null');
    const email = String(u?.email || '').toLowerCase();
    isAdmin = !!(u && (u.is_admin || DEFAULT_ADMIN_EMAILS.has(email)));
  } catch { /* noop */ }
  const { data: state, loading } = useMeState();
  const { pet: realPet, loggedIn, hasNoPet } = useMyPet();
  const storedPet = getStoredPet();
  const personaId = getStoredPersona() || DEFAULT_PERSONA;
  // "Companion" persona (default for new visitors) means the user explicitly
  // *does not assume* pet ownership — they want to discover/help/learn.
  // We always show them the discovery hero, even when logged in.
  const isCompanionPersona = personaId === 'companion';
  // Guest = not logged in AND hasn't completed onboarding (no stored pet)
  // Guests get a clearly-non-ownership welcome (not a fake pet pretending to be theirs)
  const isGuest = !loggedIn && !storedPet;
  // Show the discovery (non-ownership) hero whenever the user is either a
  // pure guest OR has self-identified as the companion persona.
  const showDiscoveryHero = isGuest || isCompanionPersona;
  // Priority: real pet (logged in) → stored pet from onboarding → null (guest, no fake pet)
  const pet = realPet || (loggedIn ? null : storedPet);
  const greeting = hasNoPet
    ? 'ยินดีต้อนรับ! เพิ่มน้องตัวแรกเพื่อเริ่มใช้งาน 🐾'
    : 'หิวแล้วอ่ะ 🍖 วันนี้กินอะไร?';

  // ── Real nearby pets (with photo thumbnails) — no fake fallback before marketing ──
  const [nearbyPets, setNearbyPets] = useState(null);
  const [homeConfig, setHomeConfig] = useState(null);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet(`${V2_API}/home-config`);
        if (!cancelled) {
          const config = data.config || {};
          setHomeConfig(config);
          setAnnouncementOpen(Boolean(config.announcement_enabled && (config.announcement_title || config.announcement_body || config.announcement_image)));
        }
      } catch {
        if (!cancelled) setHomeConfig({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchNearby = async (lat = 13.7563, lng = 100.5018, span = 0.25) => {
      const hidden = new Set((homeConfig?.hidden_animal_ids || []).map(String));
      const params = new URLSearchParams({
        north: String(lat + span), south: String(lat - span), east: String(lng + span), west: String(lng - span),
        lat: String(lat), lng: String(lng), limit: '12',
        include_thumbnail: 'true', include_hidden: 'false',
      });
      const list = await apiGet(`${API}/animals/map/markers?${params}`);
      if (cancelled) return;
      const withPhotos = (Array.isArray(list) ? list : [])
        .filter(a => a.thumbnail && a.name)
        .filter(a => !hidden.has(String(a.id)))
        .filter(a => !a.community_hidden && !['needs_review', 'community_hidden'].includes(a.moderation_status))
        .filter(a => isMarketingSafeAnimalName(a.name))
        .slice(0, 8)
        .map((a, i) => ({
          id: a.id || `r-${i}`,
          name: a.name,
          species: (a.species || 'dog').toLowerCase().includes('cat') ? 'cat' : 'dog',
          dist: a.distance != null ? `${(a.distance / 1000).toFixed(1)} กม.` : 'บนแผนที่',
          mood: pickMood(a.name),
          photo: a.thumbnail,
          lat: a.location?.lat,
          lng: a.location?.lng,
          moderation_status: a.moderation_status || 'ok',
          pin_trust_score: a.pin_trust_score || 0,
        }));
      setNearbyPets(orderByPinned(withPhotos, homeConfig?.pinned_animal_ids));
    };
    (async () => {
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => fetchNearby(pos.coords.latitude, pos.coords.longitude, 0.18).catch(() => !cancelled && setNearbyPets([])),
            () => fetchNearby().catch(() => !cancelled && setNearbyPets([])),
            { enableHighAccuracy: false, timeout: 3500, maximumAge: 300000 },
          );
        } else {
          await fetchNearby();
        }
      } catch {
        if (!cancelled) setNearbyPets([]);
      }
    })();
    return () => { cancelled = true; };
  }, [homeConfig]);
  const nearby = nearbyPets || [];
  const [activity, setActivity] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(true);
  const [stampHunt, setStampHunt] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ loading: true, data: null, allLeaderboards: [] });
  const [weeklyChamps, setWeeklyChamps] = useState(null);
  const [boostNotifs, setBoostNotifs] = useState({ list: [], unread: 0 });
  const [championshipEnabled, setChampionshipEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiGet(`${V2_API}/leaderboards/config`);
        if (!cancelled) setChampionshipEnabled(cfg?.enabled !== false);
      } catch { /* default to enabled */ }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const posts = await apiGet(`${API}/posts`);
        if (cancelled) return;
        const hidden = new Set((homeConfig?.hidden_post_ids || []).map(String));
        const mapped = (Array.isArray(posts) ? posts : [])
          .filter(p => p && p.status !== 'deleted' && p.moderation_status !== 'community_hidden')
          .filter(p => !hidden.has(String(p.id)))
          .filter(isMarketingSafePost)
          .map((p, index) => ({ post: p, score: communityScore(p), index }))
          .sort((a, b) => {
            const pins = (homeConfig?.pinned_post_ids || []).map(String);
            const ai = pins.indexOf(String(a.post.id));
            const bi = pins.indexOf(String(b.post.id));
            if (ai >= 0 && bi >= 0) return ai - bi;
            if (ai >= 0) return -1;
            if (bi >= 0) return 1;
            return b.score - a.score || a.index - b.index;
          })
          .slice(0, 6)
          .map(({ post }, index) => activityFromPost(post, index));
        setActivity(mapped);
      } catch {
        if (!cancelled) setActivity([]);
      }
    })();
    return () => { cancelled = true; };
  }, [homeConfig]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet(`${V2_API}/challenges/current`);
        if (!cancelled) setChallenge(data.challenge || null);
      } catch {
        if (!cancelled) setChallenge(null);
      } finally {
        if (!cancelled) setChallengeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet(`${V2_API}/stamp-hunts?home=1&t=${Date.now()}`);
        const hunt = (data.hunts || []).find(h => h.slug === 'lumpini-cat-stamp-hunt' && h.status === 'active') || null;
        if (!cancelled) setStampHunt(hunt);
      } catch {
        if (!cancelled) setStampHunt(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet(`${V2_API}/leaderboards/weekly-champions`);
        if (!cancelled) setWeeklyChamps(data?.champions || null);
      } catch {
        if (!cancelled) setWeeklyChamps(null);
      }
      try {
        if (localStorage.getItem('taillink_token')) {
          const data = await apiGet(`${V2_API}/me/boost-notifications`);
          if (!cancelled) setBoostNotifs({ list: data?.notifications || [], unread: data?.unread_count || 0 });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissBoostNotif = async (notifId) => {
    setBoostNotifs(prev => ({ ...prev, list: prev.list.filter(n => n.id !== notifId), unread: Math.max(0, prev.unread - 1) }));
    try { await apiPost(`${V2_API}/me/boost-notifications/${notifId}/read`, {}); } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    const loadLeaderboard = async (lat, lng) => {
      try {
        const qs = (lat != null && lng != null) ? `?lat=${lat}&lng=${lng}` : '';
        const [autoRes, listRes] = await Promise.all([
          apiGet(`${V2_API}/leaderboards/auto${qs}`),
          apiGet(`${V2_API}/leaderboards`),
        ]);
        const slug = autoRes?.leaderboard?.slug || 'global';
        const top = await apiGet(`${V2_API}/leaderboards/${slug}/top?limit=10`);
        if (cancelled) return;
        setLeaderboard({
          loading: false,
          data: { leaderboard: top.leaderboard, top: top.top || [] },
          allLeaderboards: listRes?.leaderboards || [],
        });
      } catch {
        if (!cancelled) setLeaderboard({ loading: false, data: null, allLeaderboards: [] });
      }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => loadLeaderboard(pos.coords.latitude, pos.coords.longitude),
        () => loadLeaderboard(null, null),
        { timeout: 5000, maximumAge: 5 * 60 * 1000 },
      );
    } else {
      loadLeaderboard(null, null);
    }
    return () => { cancelled = true; };
  }, []);

  const switchLeaderboard = async (slug) => {
    setLeaderboard(prev => ({ ...prev, loading: true }));
    try {
      const top = await apiGet(`${V2_API}/leaderboards/${slug}/top?limit=10`);
      setLeaderboard(prev => ({ ...prev, loading: false, data: { leaderboard: top.leaderboard, top: top.top || [] } }));
    } catch {
      setLeaderboard(prev => ({ ...prev, loading: false }));
    }
  };

  const joinChallenge = async () => {
    if (!challenge) return;
    try {
      const data = await apiPost(`${V2_API}/challenges/${challenge.slug}/join`, {
        participant_id: getChallengeGuestId(),
        source: 'home',
      });
      setChallenge(data.challenge || challenge);
      toast('เข้าร่วมภารกิจแล้ว! ออกไปเจอน้องจริงกัน 🧡');
    } catch {
      toast('เข้าร่วมภารกิจไม่สำเร็จ ลองใหม่อีกครั้งนะ', { tone: 'error' });
    }
  };
  const streak = state?.streak ?? 0;
  const locationLabel = state?.location_label || 'ยังไม่ได้เดินวันนี้';

  return (
    <div
      data-testid="v2-home-feed"
      className="tl-v2-fade-in tl-v2-page-wide"
      style={{
        width: '100%', minHeight: '100vh',
        background: theme.bg, color: theme.text,
        paddingBottom: 140,
      }}
    >
      {/* Top Greeting + Avatar */}
      <div style={{ paddingTop: 'max(56px, env(safe-area-inset-top))' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 22px 14px' }}>
          <div>
            <div style={{ fontFamily: UI, fontSize: 12, color: theme.muted, fontWeight: 500, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {showDiscoveryHero ? 'ยินดีต้อนรับ' : 'เช้านี้ · วันอาทิตย์'}
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, marginTop: 2, letterSpacing: -0.5 }}>
              {showDiscoveryHero
                ? <>สวัสดี! <span style={{ color: theme.primary, fontStyle: 'italic' }}>ชาว TaiLink</span></>
                : <>สวัสดี{pet ? <>, <span style={{ color: theme.primary, fontStyle: 'italic' }}>{pet.name}</span></> : '!'}</>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAdmin && (
            <button
              data-testid="v2-home-admin-zone-btn"
              onClick={() => navigate('/v2/admin')}
              title="Admin Zone"
              aria-label="Admin Zone"
              style={{
                border: `1px solid ${theme.cardBorder}`, background: theme.surface,
                color: theme.primary, cursor: 'pointer', padding: 0, width: 34, height: 34,
                borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 15,
              }}
            >⚙️</button>
            )}
            {isAdmin && (
            <button
              data-testid="v2-home-debug-btn"
              onClick={() => navigate('/v2/debug')}
              title="Diagnostic"
              aria-label="Diagnostic"
              style={{
                border: `1px solid ${theme.cardBorder}`, background: 'transparent',
                color: theme.muted, cursor: 'pointer', padding: 0, width: 34, height: 34,
                borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 15,
              }}
            >🩺</button>
            )}
            {isGuest ? (
              <button
                data-testid="v2-home-guest-login-btn"
                onClick={() => navigate('/v2/me')}
                style={{
                  border: `1.5px solid ${theme.primary}`,
                  background: 'transparent', color: theme.primary,
                  fontFamily: UI, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  padding: '8px 14px', borderRadius: 999,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"/>
                </svg>
                เข้าสู่ระบบ
              </button>
            ) : (
              <button
                data-testid="v2-home-avatar-btn"
                onClick={() => navigate('/v2/me')}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
              >
                <PetAvatar
                  size={44}
                  name={pet?.name || 'TL'}
                  species={pet?.species || 'dog'}
                  theme={theme}
                  src={resolvePhotoSrc(pet?.photo, 'medium') || undefined}
                />
              </button>
            )}
          </div>
        </div>

        {/* Hero card — discovery (guest + companion persona) / Pet of the day / Add-pet prompt */}
        <div style={{ padding: '0 16px' }}>
          {showDiscoveryHero ? (
            <SunsetGradient theme={theme} style={{ padding: 22, color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 36, lineHeight: 1 }}>🐾</span>
                <div style={{ fontFamily: UI, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, opacity: 0.88, textTransform: 'uppercase' }}>
                  TaiLink · เพื่อนสี่ขาในชุมชน
                </div>
              </div>
              <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, lineHeight: 1.2, marginTop: 10, letterSpacing: -0.4 }}>
                สวัสดี! ใกล้คุณ<br/>
                มีน้องน่ารักรออยู่ ✨
              </div>
              <div style={{ fontFamily: UI, fontSize: 13, opacity: 0.92, marginTop: 8, lineHeight: 1.5 }}>
                เปิดแผนที่ดูเลย — ค้นพบ ถ่ายรูป เก็บโมเมนต์น่ารักได้ทันที
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button
                  data-testid="v2-home-quick-spot-btn"
                  onClick={() => navigate('/v2/spot')}
                  style={{
                    flex: '1 1 auto', minWidth: 170, height: 46, borderRadius: 999, border: 'none',
                    background: 'rgba(255,255,255,0.95)', color: theme.primary,
                    fontFamily: UI, fontSize: 14, fontWeight: 800, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  📸 เจอน้องน่ารัก
                </button>
                <button
                  data-testid="v2-home-guest-map-btn"
                  onClick={() => navigate('/v2/map')}
                  style={{
                    flex: '0 1 auto', minWidth: 130, height: 46, borderRadius: 999,
                    border: '1.5px solid rgba(255,255,255,0.7)',
                    background: 'transparent', color: '#fff',
                    fontFamily: UI, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  🗺️ ดูน้องใกล้ฉัน
                </button>
                <button
                  data-testid="v2-home-guest-explore-btn"
                  onClick={() => navigate('/v2/community')}
                  style={{
                    flex: '0 1 auto', height: 46, padding: '0 16px', borderRadius: 999,
                    border: '1.5px solid rgba(255,255,255,0.7)',
                    background: 'transparent', color: '#fff',
                    fontFamily: UI, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  🎴 ลองเลือกน้อง
                </button>
              </div>
            </SunsetGradient>
          ) : hasNoPet ? (
            <SunsetGradient theme={theme} style={{ padding: 22, color: '#fff' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>🐾✨</div>
              <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, lineHeight: 1.3 }}>
                อยากแนะนำน้องให้<br/>เพื่อนๆ รู้จักไหม?
              </div>
              <div style={{ fontFamily: UI, fontSize: 13, opacity: 0.9, marginTop: 6 }}>
                เพิ่มแค่ชื่อกับรูปก็เริ่มได้เลย
              </div>
              <div style={{ marginTop: 14 }}>
                <button
                  data-testid="v2-home-add-pet-btn"
                  onClick={() => navigate('/v2/onboarding')}
                  style={{
                    height: 46, padding: '0 22px', borderRadius: 999, border: 'none',
                    background: 'rgba(255,255,255,0.95)', color: theme.primary,
                    fontFamily: UI, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  🐾 เพิ่มน้องของคุณ →
                </button>
              </div>
            </SunsetGradient>
          ) : (
          <SunsetGradient theme={theme} style={{ padding: 22, color: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <PetAvatar
                size={84}
                name={pet?.name || 'TL'}
                species={pet?.species || 'dog'}
                theme={theme}
                src={resolvePhotoSrc(pet?.photo, 'medium') || undefined}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: UI, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, opacity: 0.85, textTransform: 'uppercase' }}>
                  น้องบอกว่า...
                </div>
                <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, lineHeight: 1.25, marginTop: 4, letterSpacing: -0.3 }}>
                  "{greeting}"
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button
                data-testid="v2-home-walk-btn"
                onClick={() => navigate('/v2/map')}
                style={{
                  flex: 1, height: 46, borderRadius: 999, border: 'none',
                  background: 'rgba(255,255,255,0.95)', color: theme.primary,
                  fontFamily: UI, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <PawIcon size={16}/> ไปเดินเล่น
              </button>
              <button
                data-testid="v2-home-share-btn"
                onClick={shareApp}
                aria-label="แชร์ TailLink"
                title="แชร์ TailLink"
                style={{
                  width: 46, height: 46, borderRadius: '50%',
                  border: '1.5px solid rgba(255,255,255,0.5)',
                  background: 'transparent', color: '#fff', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              </button>
            </div>
          </SunsetGradient>
          )}
        </div>
      </div>

      <MobileInstallPrompt theme={theme} />

      {announcementOpen && homeConfig?.announcement_enabled && (homeConfig.announcement_title || homeConfig.announcement_body || homeConfig.announcement_image) && (
        <HomeAnnouncementPopup theme={theme} config={homeConfig} onClose={() => setAnnouncementOpen(false)} onNavigate={navigate} />
      )}

      {homeConfig?.announcement_enabled && (homeConfig.announcement_title || homeConfig.announcement_body) && (
        <div data-testid="v2-home-admin-announcement" style={{ padding: '0 16px 12px' }}>
          <div style={{
            border: `1px solid ${theme.cardBorder}`,
            background: `linear-gradient(135deg, ${theme.surface}, ${theme.primary}12)`,
            borderRadius: 8,
            padding: 16,
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 12,
            alignItems: 'center',
          }}>
            <div style={{ minWidth: 0 }}>
              <div data-testid="v2-home-admin-announcement-title" style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 900, color: theme.text }}>{homeConfig.announcement_title}</div>
              <div data-testid="v2-home-admin-announcement-body" style={{ fontFamily: UI, fontSize: 13, color: theme.muted, lineHeight: 1.45, marginTop: 4 }}>{homeConfig.announcement_body}</div>
            </div>
            {homeConfig.announcement_cta_label && homeConfig.announcement_cta_path && (
              <button
                data-testid="v2-home-admin-announcement-cta"
                onClick={() => navigate(homeConfig.announcement_cta_path)}
                style={{
                  height: 38, borderRadius: 999, border: 'none', background: theme.primary,
                  color: theme.primaryInk, fontFamily: UI, fontSize: 12, fontWeight: 900,
                  padding: '0 14px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >{homeConfig.announcement_cta_label}</button>
            )}
          </div>
        </div>
      )}

      {/* Streak + Location strip — hidden for discovery mode (companion + guest) */}
      {!showDiscoveryHero && (
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10, padding: '14px 16px 0' }}>
        <div
          data-testid="v2-home-streak"
          style={{
            background: theme.surface, borderRadius: 18, padding: '14px 16px',
            border: `1px solid ${theme.cardBorder}`,
            display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: `${theme.accent}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
          }}>🔥</div>
          <div>
            <div style={{ fontFamily: UI, fontSize: 11, color: theme.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Streak
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 600, lineHeight: 1, marginTop: 2 }}>
              {streak}{' '}
              <span style={{ fontSize: 13, color: theme.muted, fontFamily: UI, fontWeight: 500 }}>วัน</span>
            </div>
          </div>
        </div>

        <div
          data-testid="v2-home-location"
          style={{
            background: theme.surface, borderRadius: 18, padding: '14px 16px',
            border: `1px solid ${theme.cardBorder}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <div style={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
            <div style={{ position: 'absolute', inset: 3, background: theme.safe, borderRadius: '50%' }}/>
            <div className="tl-v2-pulse-ring" style={{ position: 'absolute', inset: 0, border: `2px solid ${theme.safe}`, borderRadius: '50%' }}/>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: UI, fontSize: 10, color: theme.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>อยู่ที่</div>
            <div style={{ fontFamily: UI, fontSize: 13, fontWeight: 600, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {locationLabel}
            </div>
          </div>
        </div>
      </div>
      )}

      <CommunityChallengeCard theme={theme} challenge={challenge} loading={challengeLoading} onJoin={joinChallenge} />

      {stampHunt?.status === 'active' && (
        <div data-testid="v2-home-stamp-hunt-wrapper" style={{ margin: '18px 16px 0' }}>
          <button
            data-testid="v2-home-stamp-hunt-card"
            onClick={() => navigate('/v2/stamp-hunts/lumpini-cat-stamp-hunt')}
            style={{ position: 'relative', width: '100%', border: '4px solid #0A0A0A', borderRadius: 28, padding: 0, textAlign: 'left', cursor: 'pointer', color: '#0A0A0A', background: '#FFFFFF', boxShadow: '6px 6px 0 #0A0A0A', overflow: 'hidden', display: 'block', animation: 'tlHuntFloat 4.2s ease-in-out infinite' }}
          >
            <style>{`@keyframes tlHuntFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}@keyframes tlHuntPulse{0%{transform:scale(.92);opacity:.55}70%{transform:scale(1.5);opacity:0}100%{opacity:0}}`}</style>
            {/* Hero cover strip */}
            <div style={{ position: 'relative', height: 124, background: `linear-gradient(135deg, rgba(255,200,55,.55), rgba(255,107,107,.55)), url(${stampHunt.cover_photo || 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=900&q=70'}) center/cover`, borderBottom: '4px solid #0A0A0A' }}>
              <div style={{ position: 'absolute', top: 12, left: 12, padding: '5px 10px', borderRadius: 999, background: '#FF6B6B', border: '3px solid #0A0A0A', boxShadow: '3px 3px 0 #0A0A0A', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFFFFF', boxShadow: '0 0 0 3px rgba(255,255,255,.35)' }} />
                <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 900, color: '#FFFFFF', letterSpacing: 0.4 }}>LIVE · OPEN</span>
              </div>
              <div style={{ position: 'absolute', top: 12, right: 12, padding: '5px 10px', borderRadius: 999, background: '#FFFFFF', border: '3px solid #0A0A0A', boxShadow: '3px 3px 0 #0A0A0A', fontFamily: MONO, fontSize: 9, fontWeight: 900, letterSpacing: 0.4 }}>📍 {stampHunt.area_name || 'STAMP HUNT'}</div>
              <div style={{ position: 'absolute', right: 14, bottom: -22, width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(160deg, #FFC837, #4FB0C6)', border: '4px solid #0A0A0A', boxShadow: '4px 4px 0 #0A0A0A', display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 16, fontWeight: 900, color: '#0A0A0A' }}>
                {stampHunt.collected_count || 0}/{stampHunt.target_count || 0}
              </div>
            </div>
            {/* Content */}
            <div style={{ padding: '16px 18px 18px', paddingRight: 90 }}>
              <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 900, color: '#FF6B6B', letterSpacing: 0.4 }}>STAMP HUNT MISSION</div>
              <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 900, lineHeight: 1.04, marginTop: 4, letterSpacing: -0.3 }}>
                {stampHunt.title || 'ล่าแสตมป์แมวสวนลุม'}
              </div>
              <div style={{ fontFamily: UI, fontSize: 13, fontWeight: 600, lineHeight: 1.45, marginTop: 6, color: '#3F3A33' }}>
                {stampHunt.description || 'เปิด Map Game Overlay แล้วสะสมแสตมป์จากหมุดแมวจริงในชุมชน'}
              </div>
              {/* Paw progress strip */}
              <div data-testid="v2-home-stamp-hunt-paws" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 12, fontFamily: MONO, fontSize: 13, fontWeight: 900 }}>
                {Array.from({ length: Math.max(stampHunt.target_count || 0, 1) }).slice(0, 10).map((_, i) => (
                  <span key={i} style={{ opacity: i < (stampHunt.collected_count || 0) ? 1 : .28, color: i < (stampHunt.collected_count || 0) ? '#E07A5F' : '#0A0A0A' }}>🐾</span>
                ))}
                <span style={{ marginLeft: 6, fontSize: 11, color: '#3F3A33' }}>{stampHunt.collected_count || 0}/{stampHunt.target_count || 0} เป้าหมาย</span>
              </div>
              {/* CTA + meta row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <div data-testid="v2-home-stamp-hunt-progress" style={{ padding: '9px 14px', borderRadius: 999, border: '3px solid #0A0A0A', background: '#FFC837', boxShadow: '3px 3px 0 #0A0A0A', fontFamily: UI, fontSize: 12, fontWeight: 900, color: '#0A0A0A' }}>
                  เริ่มล่าแสตมป์ →
                </div>
                {Number.isFinite(Number(stampHunt.require_gps_radius_m)) && Number(stampHunt.require_gps_radius_m) > 0 && (
                  <div style={{ padding: '7px 11px', borderRadius: 999, border: '3px solid #0A0A0A', background: '#FFFFFF', fontFamily: MONO, fontSize: 10, fontWeight: 900, letterSpacing: 0.4 }}>
                    GPS · {Number(stampHunt.require_gps_radius_m) >= 1000 ? `${(Number(stampHunt.require_gps_radius_m) / 1000).toFixed(1)} กม.` : `${Number(stampHunt.require_gps_radius_m)} ม.`}
                  </div>
                )}
              </div>
            </div>
          </button>
        </div>
      )}

      <BoostNotifBanner notifs={boostNotifs} navigate={navigate} onDismiss={dismissBoostNotif} />
      {championshipEnabled && <BoostLeaderboardCard leaderboard={leaderboard} onSwitch={switchLeaderboard} navigate={navigate} />}
      {championshipEnabled && <WeeklyChampionsCard champions={weeklyChamps} navigate={navigate} />}

      <CommunityActivityBoard theme={theme} activity={activity} />

      {/* น้องใกล้ๆ — real animals nearby with photo previews */}
      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 22px 12px' }}>
          <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>น้องใกล้ๆ</div>
          <button
            data-testid="v2-home-nearby-all"
            onClick={() => navigate('/v2/map')}
            style={{ border: 'none', background: 'transparent', color: theme.primary, fontFamily: UI, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            ดูทั้งหมด →
          </button>
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '0 16px', overflowX: 'auto' }}>
          {nearbyPets === null && [0,1,2].map(i => (
            <div key={`nearby-skeleton-${i}`} data-testid={`v2-home-nearby-skeleton-${i}`} style={{
              flexShrink: 0, width: 150, height: 228, borderRadius: 22,
              background: theme.skeleton, opacity: 0.72,
            }}/>
          ))}
          {nearbyPets && nearbyPets.length === 0 && (
            <div data-testid="v2-home-nearby-empty" style={{
              width: '100%', minWidth: 260, background: theme.surface,
              borderRadius: 22, border: `1px dashed ${theme.cardBorder}`,
              padding: 18, fontFamily: UI, color: theme.muted, lineHeight: 1.5,
            }}>
              <strong style={{ color: theme.text }}>ยังไม่มีน้องจริงใกล้พื้นที่นี้</strong><br/>
              เพิ่มข้อมูลน้องจริงหรือ seed community ก่อนเริ่มยิงแคมเปญ
            </div>
          )}
          {nearby.map(p => {
            const photoUrl = photoSrcOf(p.photo);
            return (
              <div
                key={p.id}
                data-testid={`v2-home-nearby-${p.id}`}
                style={{
                  flexShrink: 0, width: 150, borderRadius: 22,
                  background: theme.surface, border: `1px solid ${theme.cardBorder}`,
                  overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                {/* Photo area — real thumbnail if available, otherwise illustrated avatar */}
                <div style={{
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  background: photoUrl
                    ? theme.skeleton
                    : `linear-gradient(135deg, ${theme.sunsetA}22, ${theme.sunsetB}22)`,
                  overflow: 'hidden',
                }}>
                  {photoUrl && (
                    <img
                      src={photoUrl}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      width="150"
                      height="150"
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%', objectFit: 'cover',
                      }}
                    />
                  )}
                  {!photoUrl && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PetAvatar size={72} name={p.name} species={p.species} theme={theme}/>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(255,255,255,0.95)', borderRadius: '50%',
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}>{p.mood}</div>
                  {p.id && !String(p.id).startsWith('m') && (
                    <button
                      data-testid={`v2-home-nearby-share-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); sharePet(p); }}
                      aria-label={`แชร์น้อง ${p.name}`}
                      title="แชร์น้อง"
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 30, height: 30, borderRadius: '50%', border: 'none',
                        background: 'rgba(255,255,255,0.94)', color: theme.primary,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.16)', cursor: 'pointer',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 10.7l6.8-4.4M8.6 13.3l6.8 4.4"/>
                      </svg>
                    </button>
                  )}
                </div>
                <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: theme.text,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center',
                  }}>{p.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: theme.muted, marginTop: 2 }}>{p.dist}</div>
                  <button
                    data-testid={`v2-home-nearby-view-${p.id}`}
                    onClick={() => {
                      // Pass pet id (and coords as a hint) so MapV2 can fly
                      // to the pet AND auto-open its detail sheet.
                      const params = new URLSearchParams({ pet: String(p.id) });
                      if (p.lat) params.set('lat', String(p.lat));
                      if (p.lng) params.set('lng', String(p.lng));
                      navigate(`/v2/map?${params.toString()}`);
                    }}
                    style={{
                      marginTop: 10, height: 30, padding: '0 14px', borderRadius: 999,
                      background: 'transparent', border: `1.5px solid ${theme.primary}`,
                      color: theme.primary, fontFamily: UI, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >ดูใกล้ๆ →</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* "รู้ไหม?" — rotating playful pet trivia (replaces Pokdeng banner; Pokdeng is now a secret menu) */}
      <TriviaCard theme={theme}/>
    </div>
  );
}

function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const uaDataMobile = navigator.userAgentData?.mobile;
  return Boolean(uaDataMobile) || /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua);
}

function isStandaloneApp() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function getInstallOs() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'mobile';
}

function MobileInstallPrompt({ theme }) {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  const os = typeof navigator === 'undefined' ? 'mobile' : getInstallOs();

  useEffect(() => {
    if (!isMobileDevice() || isStandaloneApp() || localStorage.getItem('tl_install_dismissed') === '1') return;
    setVisible(true);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
    }
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      setSheetOpen(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const startInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice.catch(() => null);
      setDeferredPrompt(null);
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
        setVisible(false);
        toast('ติดตั้ง TailLink แล้ว 🐾');
      } else {
        setSheetOpen(true);
      }
      return;
    }
    setSheetOpen(true);
  };

  const dismiss = () => {
    localStorage.setItem('tl_install_dismissed', '1');
    setVisible(false);
    setSheetOpen(false);
  };

  if (!visible || installed) return null;

  return (
    <div data-testid="v2-mobile-install-card" style={{ padding: '0 16px 12px' }}>
      <div style={{
        border: `1px solid ${theme.cardBorder}`,
        background: theme.surface,
        borderRadius: 22,
        padding: 12,
        display: 'grid',
        gridTemplateColumns: '42px 1fr auto',
        gap: 10,
        alignItems: 'center',
        boxShadow: '0 12px 28px rgba(20,26,38,0.08)',
      }}>
        <div style={{ width: 42, height: 42, borderRadius: 14, display: 'grid', placeItems: 'center', background: `${theme.primary}18`, fontSize: 22 }}>📲</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 900, color: theme.text }}>ติดตั้ง TailLink</div>
          <div style={{ fontFamily: UI, fontSize: 11, color: theme.muted, lineHeight: 1.35 }}>เปิดเร็วเหมือนแอป เก็บไว้บนหน้าจอมือถือ</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            data-testid="v2-mobile-install-button"
            onClick={startInstall}
            style={{
              height: 38,
              border: 'none',
              borderRadius: 999,
              padding: '0 13px',
              background: theme.primary,
              color: '#fff',
              fontFamily: UI,
              fontSize: 12,
              fontWeight: 900,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >ติดตั้ง</button>
          <button
            data-testid="v2-mobile-install-dismiss"
            onClick={dismiss}
            aria-label="ซ่อนปุ่มติดตั้ง"
            title="ซ่อน"
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: `1px solid ${theme.cardBorder}`,
              background: theme.bg,
              color: theme.muted,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >×</button>
        </div>
      </div>
      {sheetOpen && (
        <InstallHelpSheet
          theme={theme}
          os={os}
          hasNativePrompt={Boolean(deferredPrompt)}
          onInstall={startInstall}
          onClose={() => setSheetOpen(false)}
          onDismiss={dismiss}
        />
      )}
    </div>
  );
}

function InstallHelpSheet({ theme, os, hasNativePrompt, onInstall, onClose, onDismiss }) {
  const steps = os === 'ios'
    ? [
      'กดปุ่มแชร์ใน Safari',
      'เลือก “เพิ่มไปยังหน้าจอโฮม”',
      'กด “เพิ่ม” แล้ว TailLink จะอยู่บนหน้าจอมือถือ',
    ]
    : os === 'android'
      ? [
        hasNativePrompt ? 'กด “ติดตั้งทันที” เพื่อเปิดหน้าต่างติดตั้ง' : 'เปิดเมนู ⋮ ของ Chrome/Browser',
        'เลือก “ติดตั้งแอป” หรือ “เพิ่มไปยังหน้าจอหลัก”',
        'ยืนยัน แล้วเปิด TailLink จากไอคอนใหม่ได้เลย',
      ]
      : [
        'เปิดเมนูของเบราว์เซอร์บนมือถือ',
        'เลือกติดตั้งแอปหรือเพิ่มไปยังหน้าจอหลัก',
        'ยืนยันเพื่อสร้างไอคอน TailLink',
      ];
  return createPortal(
    <div data-testid="v2-mobile-install-sheet" style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(20,26,38,0.42)', display: 'flex', alignItems: 'flex-end', padding: 12 }}>
      <div style={{ width: '100%', maxWidth: 520, margin: '0 auto', borderRadius: 26, background: theme.surface, border: `1px solid ${theme.cardBorder}`, boxShadow: '0 24px 70px rgba(0,0,0,0.25)', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 900, color: theme.text }}>ติดตั้งบนมือถือ</div>
            <div style={{ fontFamily: UI, fontSize: 12, color: theme.muted, marginTop: 4 }}>วิธีที่เร็วที่สุดสำหรับ {os === 'ios' ? 'iPhone / iPad' : os === 'android' ? 'Android' : 'มือถือเครื่องนี้'}</div>
          </div>
          <button data-testid="v2-mobile-install-sheet-close" onClick={onClose} aria-label="ปิดวิธีติดตั้ง" style={{ width: 38, height: 38, borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: theme.bg, color: theme.text, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ display: 'grid', gap: 9, marginTop: 14 }}>
          {steps.map((step, i) => (
            <div key={step} data-testid={`v2-mobile-install-step-${i}`} style={{ display: 'grid', gridTemplateColumns: '30px 1fr', gap: 10, alignItems: 'center', padding: 10, borderRadius: 16, background: theme.bg, border: `1px solid ${theme.cardBorder}` }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', background: theme.primary, color: '#fff', fontFamily: MONO, fontSize: 12, fontWeight: 900 }}>{i + 1}</div>
              <div style={{ fontFamily: UI, fontSize: 13, color: theme.text, fontWeight: 800, lineHeight: 1.4 }}>{step}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          {hasNativePrompt && (
            <button data-testid="v2-mobile-install-native-prompt" onClick={onInstall} style={{ flex: 1, height: 44, borderRadius: 999, border: 'none', background: theme.primary, color: '#fff', fontFamily: UI, fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>ติดตั้งทันที</button>
          )}
          <button data-testid="v2-mobile-install-sheet-later" onClick={onDismiss} style={{ flex: 1, height: 44, borderRadius: 999, border: `1px solid ${theme.cardBorder}`, background: theme.bg, color: theme.text, fontFamily: UI, fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>ไว้ทีหลัง</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function HomeAnnouncementPopup({ theme, config, onClose, onNavigate }) {
  const go = () => {
    if (config.announcement_cta_path) onNavigate(config.announcement_cta_path);
    onClose();
  };
  return createPortal(
    <div data-testid="v2-home-announcement-popup" style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(20,26,38,0.44)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 460, borderRadius: 30, overflow: 'hidden', background: theme.surface, border: `1px solid ${theme.cardBorder}`, boxShadow: '0 28px 90px rgba(0,0,0,.28)' }}>
        {config.announcement_image && <div data-testid="v2-home-announcement-popup-image" style={{ aspectRatio: '16 / 9', background: `url(${config.announcement_image}) center/cover`, position: 'relative' }}><button data-testid="v2-home-announcement-popup-close-image" onClick={onClose} aria-label="ปิดประกาศ" style={{ position: 'absolute', top: 12, right: 12, width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(255,255,255,.5)', background: 'rgba(0,0,0,.38)', color: '#fff', cursor: 'pointer', fontSize: 22 }}>×</button></div>}
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: theme.primary, fontWeight: 900 }}>TAILLINK NOTICE</div>
              <div data-testid="v2-home-announcement-popup-title" style={{ fontFamily: SERIF, fontSize: 26, lineHeight: 1.08, fontWeight: 900, color: theme.text, marginTop: 4 }}>{config.announcement_title || 'ประกาศจาก TailLink'}</div>
            </div>
            {!config.announcement_image && <button data-testid="v2-home-announcement-popup-close" onClick={onClose} aria-label="ปิดประกาศ" style={{ width: 38, height: 38, borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: theme.bg, color: theme.text, cursor: 'pointer', fontSize: 22 }}>×</button>}
          </div>
          {config.announcement_body && <div data-testid="v2-home-announcement-popup-body" style={{ fontFamily: UI, fontSize: 14, color: theme.muted, lineHeight: 1.55, marginTop: 10 }}>{config.announcement_body}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {config.announcement_cta_label && config.announcement_cta_path && <button data-testid="v2-home-announcement-popup-cta" onClick={go} style={{ flex: 1, height: 44, borderRadius: 999, border: 'none', background: theme.primary, color: '#fff', fontFamily: UI, fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>{config.announcement_cta_label}</button>}
            <button data-testid="v2-home-announcement-popup-later" onClick={onClose} style={{ flex: config.announcement_cta_label ? '0 0 110px' : 1, height: 44, borderRadius: 999, border: `1px solid ${theme.cardBorder}`, background: theme.bg, color: theme.text, fontFamily: UI, fontSize: 13, fontWeight: 900, cursor: 'pointer' }}>ปิด</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Rotating trivia card — auto-cycles every 6s, also tap-to-next
function TriviaCard({ theme }) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * MOCK_TRIVIA.length));
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % MOCK_TRIVIA.length), 6000);
    return () => clearInterval(t);
  }, []);
  const fact = MOCK_TRIVIA[idx];
  return (
    <div style={{ padding: '22px 16px 0' }}>
      <button
        data-testid="v2-home-trivia"
        onClick={() => setIdx(i => (i + 1) % MOCK_TRIVIA.length)}
        style={{
          width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
          background: `linear-gradient(135deg, ${theme.surface}, ${theme.bg})`,
          borderRadius: 22, padding: '16px 18px',
          borderTop: `1px solid ${theme.cardBorder}`,
          borderRight: `1px solid ${theme.cardBorder}`,
          borderBottom: `1px solid ${theme.cardBorder}`,
          borderLeft: `1px solid ${theme.cardBorder}`,
          display: 'flex', alignItems: 'center', gap: 14,
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: 16, flexShrink: 0,
          background: `${theme.accent}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
        }}>{fact.emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: UI, fontSize: 10, color: theme.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            รู้ไหม?
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 500, color: theme.text, marginTop: 3, lineHeight: 1.45 }}>
            {fact.text}
          </div>
        </div>
        <div style={{ color: theme.muted, fontSize: 16, flexShrink: 0 }}>↻</div>
      </button>
    </div>
  );
}


// ─── Boost Leaderboard (Pet Champion Podium) ────────────────────────────
// Top 3 pets shown as a crown podium (gold/silver/bronze), ranks 4-10 in a
// compact list below. Tapping any pet opens its profile (with ?ref=boost so
// the boost CTA is highlighted). Dropdown lets users switch between
// admin-curated regional leaderboards (e.g. local Bangkok vs national).
const CROWN_STYLES = {
  1: { medal: '🥇', tint: '#FFC837', glow: '0 0 28px rgba(255,200,55,.55)', label: 'มงทอง' },
  2: { medal: '🥈', tint: '#C0C0C0', glow: '0 0 18px rgba(192,192,192,.45)', label: 'มงเงิน' },
  3: { medal: '🥉', tint: '#CD7F32', glow: '0 0 18px rgba(205,127,50,.45)', label: 'มงทองแดง' },
};

function PodiumPet({ pet, rank, onClick }) {
  const c = CROWN_STYLES[rank];
  // Clear visual hierarchy: 1st >>> 2nd >>> 3rd
  const size = rank === 1 ? 110 : rank === 2 ? 84 : 64;
  const heightOffset = rank === 1 ? 0 : rank === 2 ? 22 : 38;
  const nameSize = rank === 1 ? 17 : rank === 2 ? 13 : 11;
  const medalSize = rank === 1 ? 36 : rank === 2 ? 24 : 20;
  const scoreSize = rank === 1 ? 13 : rank === 2 ? 11 : 10;
  return (
    <button
      data-testid={`v2-podium-rank-${rank}`}
      onClick={() => onClick(pet)}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginTop: heightOffset, animation: rank === 1 ? 'tlPodiumFloat 3s ease-in-out infinite' : 'none' }}
    >
      <style>{`@keyframes tlPodiumFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}@keyframes tlPodiumShine{0%,100%{box-shadow:${c.glow}}50%{box-shadow:0 0 36px ${c.tint}aa}}`}</style>
      <div style={{ fontSize: medalSize, lineHeight: 1, transform: rank === 1 ? 'translateY(4px)' : 'none' }}>{c.medal}</div>
      <div style={{ position: 'relative', width: size, height: size, borderRadius: '50%', border: `${rank === 1 ? 5 : rank === 2 ? 4 : 3}px solid ${c.tint}`, overflow: 'hidden', background: '#F5EFE5', boxShadow: c.glow, animation: rank === 1 ? 'tlPodiumShine 2.2s ease-in-out infinite' : 'none' }}>
        {pet.photo ? <img src={pet.photo} alt={pet.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} /> : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: size * 0.35 }}>🐾</div>}
      </div>
      <div style={{ fontFamily: SERIF, fontSize: nameSize, fontWeight: 900, color: '#0A0A0A', maxWidth: size + 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pet.name}</div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: rank === 1 ? '4px 12px' : rank === 2 ? '3px 10px' : '2px 8px', borderRadius: 999, border: `${rank === 1 ? 3 : 2}px solid #0A0A0A`, background: c.tint, fontFamily: MONO, fontSize: scoreSize, fontWeight: 900, color: '#0A0A0A', boxShadow: rank === 1 ? '2px 2px 0 #0A0A0A' : '1px 1px 0 #0A0A0A' }}>
        💪 {pet.boost_score}
      </div>
    </button>
  );
}

function BoostLeaderboardCard({ leaderboard, onSwitch, navigate }) {
  const [showDropdown, setShowDropdown] = React.useState(false);
  if (leaderboard.loading) return null;
  if (!leaderboard.data) return null;
  const { leaderboard: lb, top } = leaderboard.data;
  const hasPodium = top.length > 0;
  const top3 = top.slice(0, 3);
  const rest = top.slice(3, 10);
  const goToPet = (pet) => navigate(`/v2/animal/${pet.id}?ref=boost`);
  return (
    <section data-testid="v2-home-boost-leaderboard" style={{ margin: '22px 16px 0' }}>
      <div style={{ position: 'relative', border: '4px solid #0A0A0A', borderRadius: 28, background: 'linear-gradient(180deg, #FFF7E0 0%, #FFFFFF 60%)', padding: '18px 16px 20px', boxShadow: '6px 6px 0 #0A0A0A', overflow: 'visible' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 900, color: '#FF6B6B', letterSpacing: 0.5 }}>🏆 BOOST CHAMPIONSHIP</div>
            <div data-testid="v2-home-boost-leaderboard-name" style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 900, lineHeight: 1.05, marginTop: 4, letterSpacing: -0.3 }}>{lb.name}</div>
            {lb.subtitle && <div style={{ fontFamily: 'system-ui', fontSize: 12, color: '#3F3A33', marginTop: 4 }}>{lb.subtitle}</div>}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              data-testid="v2-home-boost-leaderboard-switch"
              onClick={() => setShowDropdown(s => !s)}
              style={{ padding: '7px 12px', borderRadius: 999, border: '3px solid #0A0A0A', background: '#FFFFFF', fontFamily: MONO, fontSize: 10, fontWeight: 900, cursor: 'pointer', boxShadow: '2px 2px 0 #0A0A0A', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {lb.region_type === 'local' ? '📍 ในย่านนี้' : '🐾 ทุกพื้นที่'} ▾
            </button>
            {showDropdown && leaderboard.allLeaderboards.length > 0 && (
              <div data-testid="v2-home-boost-leaderboard-dropdown" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 200, background: '#FFFFFF', border: '3px solid #0A0A0A', borderRadius: 14, boxShadow: '4px 4px 0 #0A0A0A', zIndex: 20, overflow: 'hidden' }}>
                {leaderboard.allLeaderboards.map(item => (
                  <button
                    key={item.slug}
                    data-testid={`v2-home-boost-switch-${item.slug}`}
                    onClick={() => { setShowDropdown(false); onSwitch(item.slug); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #0A0A0A20', background: item.slug === lb.slug ? '#FFF3DA' : '#FFFFFF', cursor: 'pointer', fontFamily: 'system-ui', fontSize: 12, fontWeight: 700, color: '#0A0A0A', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {item.region_type === 'local' ? '📍' : '🐾'} {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Podium top 3 */}
        {hasPodium ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 8, marginTop: 18, minHeight: 220 }}>
            {[2, 1, 3].map(rankSlot => {
              const pet = top3.find(p => p.rank === rankSlot);
              if (pet) return <PodiumPet key={pet.id} pet={pet} rank={pet.rank} onClick={goToPet} />;
              // Greyed-out placeholder so the podium metaphor reads even when
              // only 1-2 pets have boosts (early-stage data).
              const c = CROWN_STYLES[rankSlot];
              const size = rankSlot === 1 ? 110 : rankSlot === 2 ? 84 : 64;
              const heightOffset = rankSlot === 1 ? 0 : rankSlot === 2 ? 22 : 38;
              const medalSize = rankSlot === 1 ? 36 : rankSlot === 2 ? 24 : 20;
              return (
                <div key={`empty-${rankSlot}`} data-testid={`v2-podium-rank-${rankSlot}-empty`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: .35, marginTop: heightOffset }}>
                  <div style={{ fontSize: medalSize, lineHeight: 1, filter: 'grayscale(1)' }}>{c.medal}</div>
                  <div style={{ width: size, height: size, borderRadius: '50%', border: '4px dashed #0A0A0A', background: '#F5EFE5', display: 'grid', placeItems: 'center', fontSize: size * 0.35 }}>🐾</div>
                  <div style={{ fontFamily: MONO, fontSize: rankSlot === 1 ? 11 : 10, fontWeight: 900, color: '#0A0A0A' }}>รอน้องคนเก่ง</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div data-testid="v2-home-boost-leaderboard-empty" style={{ marginTop: 18, padding: '24px 14px', textAlign: 'center', background: '#FFFFFF', border: '3px dashed #0A0A0A', borderRadius: 18 }}>
            <div style={{ fontSize: 40 }}>👑</div>
            <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 900, marginTop: 6 }}>ยังไม่มีน้องคนไหนติดมง</div>
            <div style={{ fontFamily: 'system-ui', fontSize: 12, color: '#3F3A33', marginTop: 4, lineHeight: 1.5 }}>
              เปิดโปรไฟล์น้องที่ชอบ แล้วกด 💪 ดันลูก<br/>เพื่อช่วยส่งเขาขึ้นมง!
            </div>
            <button
              data-testid="v2-home-boost-leaderboard-cta-map"
              onClick={() => navigate('/v2/community')}
              style={{ marginTop: 14, padding: '10px 18px', borderRadius: 999, border: '3px solid #0A0A0A', background: '#FFC837', boxShadow: '3px 3px 0 #0A0A0A', fontFamily: 'system-ui', fontSize: 13, fontWeight: 900, cursor: 'pointer' }}
            >เริ่มปัดน้อง · ดันมงคนแรก →</button>
          </div>
        )}

        {/* Ranks 4-10 */}
        {rest.length > 0 && (
          <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
            {rest.map(pet => (
              <button
                key={pet.id}
                data-testid={`v2-podium-rank-${pet.rank}`}
                onClick={() => goToPet(pet)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '2px solid #0A0A0A', borderRadius: 14, background: '#FFFFFF', cursor: 'pointer', boxShadow: '2px 2px 0 #0A0A0A', textAlign: 'left' }}
              >
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#0A0A0A', color: '#FFC837', display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 13, fontWeight: 900, flexShrink: 0 }}>{pet.rank}</div>
                <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', background: '#F5EFE5', border: '2px solid #0A0A0A', flexShrink: 0 }}>
                  {pet.photo ? <img src={pet.photo} alt={pet.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 16 }}>🐾</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'system-ui', fontSize: 13, fontWeight: 900, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pet.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: '#3F3A33', marginTop: 2 }}>{pet.species || 'pet'}</div>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 9px', borderRadius: 999, background: '#FFC837', border: '2px solid #0A0A0A', fontFamily: MONO, fontSize: 10, fontWeight: 900 }}>💪 {pet.boost_score}</div>
              </button>
            ))}
          </div>
        )}

        {hasPodium && (
          <button
            data-testid="v2-home-boost-leaderboard-cta-explore"
            onClick={() => navigate('/v2/community')}
            style={{ marginTop: 16, width: '100%', padding: '12px 14px', borderRadius: 999, border: '3px solid #0A0A0A', background: '#FFC837', boxShadow: '4px 4px 0 #0A0A0A', fontFamily: 'system-ui', fontSize: 14, fontWeight: 900, cursor: 'pointer' }}
          >💪 เริ่มปัดน้อง · ดันมง →</button>
        )}
      </div>
    </section>
  );
}


// ─── Weekly Champions Ceremony Card ─────────────────────────────────────
// Snapshotted every Sunday 18:00 +07 (Bangkok). Gives owners a time-bounded
// brag moment to share, plus signals to non-champions "if you keep dunking,
// you could be here next week".
function WeeklyChampionsCard({ champions, navigate }) {
  if (!champions || !champions.top3 || champions.top3.length === 0) return null;
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };
  const weekLabel = `สัปดาห์ ${fmt(champions.week_of)}`;
  return (
    <section data-testid="v2-home-weekly-champions" style={{ margin: '22px 16px 0' }}>
      <div style={{ position: 'relative', border: '4px solid #0A0A0A', borderRadius: 28, background: 'linear-gradient(160deg, #4A2D7A 0%, #8C4F9A 55%, #FFC837 100%)', padding: '18px 16px 18px', boxShadow: '6px 6px 0 #0A0A0A', color: '#FFFFFF', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 120, opacity: 0.18 }}>👑</div>
        <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 900, color: '#FFE08A', letterSpacing: 0.5 }}>👑 WEEKLY CEREMONY</div>
        <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 900, lineHeight: 1.05, marginTop: 4, letterSpacing: -0.3 }}>แชมป์{weekLabel}</div>
        <div style={{ fontFamily: 'system-ui', fontSize: 11, color: '#FFE08A', marginTop: 2 }}>{champions.leaderboard_name}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
          {[1, 2, 3].map(rankSlot => {
            const pet = champions.top3.find(p => p.rank === rankSlot);
            const medal = CROWN_STYLES[rankSlot];
            return (
              <button
                key={rankSlot}
                data-testid={`v2-home-weekly-rank-${rankSlot}`}
                onClick={() => pet && navigate(`/v2/animal/${pet.id}?ref=boost`)}
                disabled={!pet}
                style={{ background: 'rgba(255,255,255,.12)', border: `3px solid ${medal.tint}`, borderRadius: 16, padding: '10px 6px 8px', textAlign: 'center', cursor: pet ? 'pointer' : 'default', color: '#FFFFFF', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
              >
                <div style={{ fontSize: 22, lineHeight: 1 }}>{medal.medal}</div>
                <div style={{ width: 46, height: 46, borderRadius: '50%', border: `2px solid ${medal.tint}`, overflow: 'hidden', margin: '6px auto', background: 'rgba(255,255,255,.15)' }}>
                  {pet?.photo ? <img src={pet.photo} alt={pet.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 18 }}>🐾</div>}
                </div>
                <div style={{ fontFamily: 'system-ui', fontSize: 11, fontWeight: 900, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pet?.name || '—'}</div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: medal.tint, marginTop: 2 }}>💪 {pet?.boost_score ?? 0}</div>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 12, fontFamily: 'system-ui', fontSize: 11, color: '#FFE08A', lineHeight: 1.5, textAlign: 'center' }}>
          🎉 ขอแสดงความยินดี · ประกาศแชมป์ใหม่ทุกวันอาทิตย์ 18:00
        </div>
      </div>
    </section>
  );
}

// ─── Boost Notification Banner ──────────────────────────────────────────
// Surfaces "ใกล้ติดมง!" alerts at the top of HomeFeed for logged-in owners
// so they actually see when their pet is rising. Each banner is dismissible
// — clicking it marks read and routes to the pet profile to boost/share.
function BoostNotifBanner({ notifs, navigate, onDismiss }) {
  if (!notifs.list || notifs.list.length === 0) return null;
  const unread = notifs.list.filter(n => !n.read);
  if (unread.length === 0) return null;
  const top = unread[0];
  return (
    <section data-testid="v2-home-boost-notif-banner" style={{ margin: '16px 16px 0' }}>
      <button
        onClick={() => { onDismiss(top.id); navigate(`/v2/animal/${top.animal_id}?ref=boost`); }}
        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: '4px solid #0A0A0A', borderRadius: 22, background: 'linear-gradient(135deg, #FFC837, #FF6B6B)', cursor: 'pointer', boxShadow: '5px 5px 0 #0A0A0A', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 30, lineHeight: 1 }}>🚨</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 900, color: '#3F1A0A' }}>BOOST ALERT · {unread.length > 1 ? `+${unread.length - 1} more` : 'NEW'}</div>
            <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 900, color: '#0A0A0A', lineHeight: 1.2, marginTop: 2 }}>{top.title}</div>
            <div style={{ fontFamily: 'system-ui', fontSize: 12, color: '#0A0A0A', marginTop: 3 }}>{top.body}</div>
          </div>
          <div style={{ fontSize: 22, color: '#0A0A0A' }}>→</div>
        </div>
      </button>
    </section>
  );
}
